import { useEffect } from "react";

import { track } from "../analytics";
import { Footer } from "../components/Footer";
import { Nav } from "../components/Nav";
import { ScrollReveal } from "../components/ScrollReveal";
import { useSmoothScroll } from "../hooks/useSmoothScroll";

import { EventTicketForm } from "./EventTicketForm";
import { findEventBySlug, getEventPhase, isEventPast } from "./eventsConfig";

/**
 * Per-event registration page mounted at /events/<slug>.
 *
 * One URL a flyer or share message can drop someone straight into:
 * event details up top, ticket form below. The page is the form —
 * no extra click to expand.
 *
 * Past sessions render a soft "this one's behind us" notice instead
 * of the form so a stale link doesn't quietly collect dead-letter
 * registrations.
 *
 * Unknown / mistyped slugs fall back to a small not-found surface
 * with a link back to /events.
 */
export function EventRegisterPage({ slug }: { slug: string }) {
  useSmoothScroll();
  const event = findEventBySlug(slug);

  useEffect(() => {
    if (event) {
      track("event_register_page_viewed", { slug });
    }
  }, [event, slug]);

  // Anchor straight to the form on mount so a visitor handed a flyer
  // doesn't have to scroll past the event preamble to register. The
  // details still sit above and a one-flick scroll up reveals them.
  // Skip when the URL carries `?top=1` or when we're rendering the
  // past-session notice (no form to anchor to).
  useEffect(() => {
    if (!event || isEventPast(event)) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.has("top")) {
      return;
    }
    requestAnimationFrame(() => {
      const el = document.getElementById("register");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, [event]);

  if (!event) {
    return (
      <>
        <Nav />
        <main className="bg-cream">
          <section className="px-6 pt-32 pb-24 md:pt-40 md:pb-32">
            <div className="mx-auto max-w-2xl">
              <p className="eyebrow">Event not found</p>
              <h1
                className="font-display mt-5 text-warm-brown"
                style={{
                  fontSize: "clamp(2rem, 4.4vw, 3rem)",
                  lineHeight: 1.08,
                  letterSpacing: "0.004em",
                }}
              >
                We couldn't find that session.
              </h1>
              <p className="mt-6 text-base leading-[1.8] text-warm-brown-light">
                The link might be from an older flyer. Have a look at the full
                list of upcoming sessions and pick one that fits.
              </p>
              <a
                href="/events"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-warm-brown px-6 py-3 text-sm font-semibold tracking-wide text-cream transition-colors duration-300 hover:bg-gold"
              >
                See all events
                <span className="text-cream/70">→</span>
              </a>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  const phase = getEventPhase(event);
  const past = phase === "past";
  const venue = event.venue;

  return (
    <>
      <Nav />
      <main className="bg-cream">
        {/* Event detail header — what they're registering for. */}
        <section className="px-6 pt-32 pb-10 md:pt-40 md:pb-12">
          <div className="mx-auto max-w-3xl">
            <ScrollReveal>
              <a
                href="/events"
                className="inline-flex items-center gap-1.5 text-[0.8rem] font-medium tracking-wide text-warm-brown-light underline-offset-4 transition-colors duration-300 hover:text-warm-brown hover:underline"
              >
                ← All events
              </a>
            </ScrollReveal>

            <ScrollReveal delay={0.08}>
              <p className="eyebrow mt-7">
                {past
                  ? "Past session"
                  : phase === "active"
                    ? "Happening now · check in"
                    : "Community session · register"}
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.14}>
              <h1
                className="font-display mt-5 text-warm-brown"
                style={{
                  fontSize: "clamp(2.2rem, 5.2vw, 3.6rem)",
                  lineHeight: 1.06,
                  letterSpacing: "0.004em",
                }}
              >
                {event.title}
              </h1>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <p className="text-base font-semibold text-gold md:text-lg">
                  {event.dateLabel}
                </p>
                <p className="text-[15px] text-warm-brown-light md:text-base">
                  {event.timeLabel}
                </p>
              </div>
            </ScrollReveal>

            {venue ? (
              <ScrollReveal delay={0.26}>
                <div className="mt-6 border border-(--color-hairline) bg-warm-white px-5 py-5 md:px-6 md:py-6">
                  <p className="text-base font-semibold text-warm-brown md:text-lg">
                    {venue.name}
                  </p>
                  {venue.hall ? (
                    <p className="mt-1 text-sm text-warm-brown-light">
                      {venue.hall}
                    </p>
                  ) : null}
                  <p className="mt-0.5 text-sm text-warm-brown-light">
                    {venue.suburb}
                  </p>
                  <a
                    href={venue.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-[0.8rem] font-medium text-warm-brown underline-offset-4 transition-colors duration-300 hover:underline"
                  >
                    Get directions ↗
                  </a>
                </div>
              </ScrollReveal>
            ) : null}

            {event.longCopy ? (
              <ScrollReveal delay={0.32}>
                <p className="mt-7 max-w-2xl text-[15px] leading-[1.8] text-warm-brown-light md:text-base">
                  {event.longCopy}
                </p>
              </ScrollReveal>
            ) : null}
          </div>
        </section>

        {/* Form — or past-session notice. scroll-mt accounts for the
            fixed nav so the eyebrow lands just below the chrome. */}
        <section
          id="register"
          className="scroll-mt-32 px-6 pb-24 md:scroll-mt-40 md:pb-32"
        >
          <div className="mx-auto max-w-3xl">
            <ScrollReveal>
              <div className="flex items-baseline gap-4">
                <p className="eyebrow">
                  {past
                    ? "This session has passed"
                    : phase === "active"
                      ? "Check in"
                      : "Your details"}
                </p>
                <span
                  className="hairline h-px flex-1 self-center"
                  aria-hidden
                />
              </div>
            </ScrollReveal>

            <div className="mt-6">
              {past ? (
                <div className="border border-(--color-hairline) bg-warm-white px-6 py-7 md:px-8 md:py-8">
                  <p className="text-[15px] leading-relaxed text-warm-brown-light md:text-base">
                    This session has already happened. Have a look at the full
                    list for an upcoming sit you can join.
                  </p>
                  <a
                    href="/events"
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-warm-brown px-6 py-3 text-sm font-semibold tracking-wide text-cream transition-colors duration-300 hover:bg-gold"
                  >
                    See all events
                    <span className="text-cream/70">→</span>
                  </a>
                </div>
              ) : (
                <ScrollReveal delay={0.08}>
                  <EventTicketForm event={event} phase={phase} />
                </ScrollReveal>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
